import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Collapse, FormControl } from 'react-bootstrap';
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons';
import { CircleDollarSign, MessageCircle, Pencil, Trash2 } from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import AlertModal from '../../../AlertModal';
import CircularProgressBar from '../../../circular-progress';
import {
  createBillingNote,
  createComment,
  deleteBillingNote,
  deleteComment,
  fetchBillingNotes,
  fetchComments,
  getAuthorInitials,
  isCompletedOrderStatus,
  showsBillingNotesSection,
  isNoteAuthor,
  updateBillingNote,
  updateComment
} from '../../../../helpers/orderNotes';
import './orderNotes.scss';
import { getUserFromLocalstrorage, isClient } from '../../../../helpers/global';
import { ABORT_ERROR } from '../../../../helpers/api';

const AVATAR_STYLE = { width: '2rem', height: '2rem' };

const NoteEntry = ({
  item,
  variant = 'comment',
  currentUserInfo,
  disabled,
  isEditing,
  editingText,
  isSaving,
  onEditStart,
  onEditCancel,
  onEditSave,
  onEditingTextChange,
  onDeleteClick
}) => {
  const timestamp = item.updatedAt || item.createdAt;
  const isBilling = variant === 'billing';
  const canEditDelete = isNoteAuthor(item, currentUserInfo);
  const wasEdited = Boolean(item.editHistory?.length);

  const metaRow = (
    <div className="d-flex align-items-center justify-content-between">
      <div className="d-flex align-items-center flex-wrap">
        <span
          className={`rounded-circle d-inline-flex align-items-center justify-content-center text-white flex-shrink-0 tinyFont font-weight-bold mr-2 ${
            isBilling ? 'billing-note-avatar' : 'bg-info'
          }`}
          style={AVATAR_STYLE}
        >
          {getAuthorInitials(item.author)}
        </span>
        <span className="font-weight-semibold midFont mr-2">{item.author}</span>
        {timestamp && <span className="text-muted small mr-2">{moment(timestamp).format('MMM D, YYYY h:mm A')}</span>}
        {wasEdited && <span className="text-muted small mr-2">Edited</span>}
        {item.source && <span className="badge note-source-badge">via {item.source}</span>}
      </div>
      {canEditDelete && !isEditing && !disabled && (
        <div className="d-flex flex-shrink-0 ml-2">
          <Button
            variant="link"
            size="sm"
            className="p-1 text-secondary"
            onClick={() => onEditStart(item)}
            title={isBilling ? 'Edit billing note' : 'Edit comment'}
          >
            <Pencil size={14} />
          </Button>
          <Button
            variant="link"
            size="sm"
            className="p-1 text-danger"
            onClick={() => onDeleteClick(item)}
            title={isBilling ? 'Delete billing note' : 'Delete comment'}
          >
            <Trash2 size={14} />
          </Button>
        </div>
      )}
    </div>
  );

  const bodyContent = isEditing ? (
    <>
      <FormControl
        as="textarea"
        size="sm"
        rows={2}
        value={editingText}
        onChange={e => onEditingTextChange(e.target.value)}
        className="mt-2 midFont"
        disabled={isSaving}
      />
      <div className="mt-2">
        <Button
          size="sm"
          variant="success"
          className="mr-1"
          onClick={onEditSave}
          disabled={isSaving || !editingText.trim()}
        >
          {isSaving && <CircularProgressBar size={1} className="mr-2" />}
          Save
        </Button>
        <Button size="sm" variant="secondary" onClick={onEditCancel} disabled={isSaving}>
          Cancel
        </Button>
      </div>
    </>
  ) : (
    <div className={isBilling ? 'billing-note-card rounded px-3 py-2 mt-2 midFont' : 'mt-2'}>{item.text}</div>
  );

  if (isBilling) {
    return (
      <div className="mb-3">
        {metaRow}
        {isEditing ? <div className="mt-2">{bodyContent}</div> : bodyContent}
      </div>
    );
  }

  return (
    <div className="border bg-light rounded px-3 py-2 mb-3 midFont">
      {metaRow}
      {bodyContent}
    </div>
  );
};

const NoteComposer = ({
  placeholder,
  buttonLabel,
  buttonVariant = 'primary',
  buttonClassName = '',
  value,
  onChange,
  onSubmit,
  submitting,
  disabled
}) => {
  const canSubmit = !disabled && !submitting && Boolean(value.trim());

  const handleKeyDown = e => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && canSubmit) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
  <div className="d-flex align-items-end mt-3">
    <FormControl
      as="textarea"
      rows={2}
      className="flex-grow-1 mr-2"
      value={value}
      onChange={e => onChange(e.target.value)}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      disabled={disabled || submitting}
    />
    <Button
      variant={buttonVariant}
      className={`text-nowrap flex-shrink-0 ${buttonClassName}`}
      disabled={!canSubmit}
      onClick={onSubmit}
    >
      {submitting && <CircularProgressBar size={1} className="mr-2" />}
      {buttonLabel}
    </Button>
  </div>
  );
};

const PanelHeader = ({ icon: Icon, iconClassName, title }) => (
  <h6 className="font-weight-bold largeFont mb-3 d-flex align-items-center">
    <Icon size={18} className={`mr-2 ${iconClassName}`} />
    {title}
  </h6>
);

const PanelCardToggle = ({ icon: Icon, iconClassName, title, count, expanded, onClick, className = '' }) => (
  <button
    type="button"
    className={`btn btn-link comments-card-toggle w-100 d-flex align-items-center justify-content-between p-0 text-left text-decoration-none shadow-none border-0 ${className}`}
    onClick={onClick}
  >
    <span className="d-flex align-items-center font-weight-bold largeFont text-dark">
      <Icon size={18} className={`mr-2 ${iconClassName}`} />
      {title} ({count})
    </span>
    <span className="d-flex align-items-center text-muted midFont">
      {expanded ? 'Collapse' : 'Expand'}
      {expanded ? <ChevronUp className="ml-1" /> : <ChevronDown className="ml-1" />}
    </span>
  </button>
);

const CommentsPanel = ({
  orderType,
  comments,
  loading,
  text,
  onTextChange,
  onSubmit,
  submitting,
  disabled,
  collapsible = false,
  expanded = true,
  onToggle,
  currentUserInfo,
  editingId,
  editingText,
  isSavingEdit,
  onEditStart,
  onEditCancel,
  onEditSave,
  onEditingTextChange,
  onDeleteClick
}) => (
  <div className="card border p-3 mb-3 shadow-sm">
    {collapsible ? (
      <PanelCardToggle
        icon={MessageCircle}
        iconClassName="text-secondary"
        title={`Comments — ${orderType}`}
        count={comments.length}
        expanded={expanded}
        onClick={onToggle}
        className={expanded ? 'mb-3' : 'mb-0'}
      />
    ) : (
      <PanelHeader icon={MessageCircle} iconClassName="text-secondary" title={`Comments — ${orderType}`} />
    )}
    <Collapse in={!collapsible || expanded}>
      <div>
        <NoteComposer
          placeholder="Add a comment..."
          buttonLabel="Post Comment"
          buttonVariant="info"
          value={text}
          onChange={onTextChange}
          onSubmit={onSubmit}
          submitting={submitting}
          disabled={disabled || Boolean(editingId)}
        />
        {loading ? (
          <div className="d-flex align-items-center text-muted midFont mt-3">
            <CircularProgressBar size={1} className="mr-2" />
            Loading comments...
          </div>
        ) : comments.length === 0 ? (
          <p className="text-muted midFont mt-3 mb-0">No comments yet.</p>
        ) : (
          <div className="mt-3">
            {comments.map(comment => (
              <NoteEntry
                key={comment._id}
                item={comment}
                variant="comment"
                currentUserInfo={currentUserInfo}
                disabled={disabled}
                isEditing={editingId === comment._id}
                editingText={editingText}
                isSaving={isSavingEdit}
                onEditStart={onEditStart}
                onEditCancel={onEditCancel}
                onEditSave={onEditSave}
                onEditingTextChange={onEditingTextChange}
                onDeleteClick={onDeleteClick}
              />
            ))}
          </div>
        )}
      </div>
    </Collapse>
  </div>
);

const BillingNotesPanel = ({
  orderType,
  billingNotes,
  loading,
  text,
  onTextChange,
  onSubmit,
  submitting,
  disabled,
  collapsible = true,
  expanded = true,
  onToggle,
  currentUserInfo,
  editingId,
  editingText,
  isSavingEdit,
  onEditStart,
  onEditCancel,
  onEditSave,
  onEditingTextChange,
  onDeleteClick
}) => (
  <div className="card border p-3 mb-3 shadow-sm billing-notes-panel">
    {collapsible ? (
      <PanelCardToggle
        icon={CircleDollarSign}
        iconClassName="billing-notes-icon"
        title={`Billing Notes — ${orderType}`}
        count={billingNotes.length}
        expanded={expanded}
        onClick={onToggle}
        className={expanded ? 'mb-3' : 'mb-0'}
      />
    ) : (
      <PanelHeader icon={CircleDollarSign} iconClassName="billing-notes-icon" title={`Billing Notes — ${orderType}`} />
    )}
    <Collapse in={!collapsible || expanded}>
      <div>
        <NoteComposer
          placeholder="Add a billing note..."
          buttonLabel="Post Billing Note"
          buttonClassName="btn-billing-orange"
          value={text}
          onChange={onTextChange}
          onSubmit={onSubmit}
          submitting={submitting}
          disabled={disabled || Boolean(editingId)}
        />
        {loading ? (
          <div className="d-flex align-items-center text-muted midFont mt-3">
            <CircularProgressBar size={1} className="mr-2" />
            Loading billing notes...
          </div>
        ) : billingNotes.length === 0 ? (
          <p className="text-muted midFont mt-3 mb-0">No billing notes yet.</p>
        ) : (
          <div className="mt-3">
            {billingNotes.map(note => (
              <NoteEntry
                key={note._id}
                item={note}
                variant="billing"
                currentUserInfo={currentUserInfo}
                disabled={disabled}
                isEditing={editingId === note._id}
                editingText={editingText}
                isSaving={isSavingEdit}
                onEditStart={onEditStart}
                onEditCancel={onEditCancel}
                onEditSave={onEditSave}
                onEditingTextChange={onEditingTextChange}
                onDeleteClick={onDeleteClick}
              />
            ))}
          </div>
        )}
      </div>
    </Collapse>
  </div>
);

const OrderNotesSection = ({ orderId, orderType, status, disabled = false, refreshKey = 0 }) => {
  const isCompleted = useMemo(() => isCompletedOrderStatus(status), [status]);
  const showBillingNotes = useMemo(() => showsBillingNotesSection(status), [status]);
  const currentUserInfo = useMemo(() => getUserFromLocalstrorage() || {}, []);

  const [comments, setComments] = useState([]);
  const [billingNotes, setBillingNotes] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [loadingBillingNotes, setLoadingBillingNotes] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [billingNoteText, setBillingNoteText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [submittingBillingNote, setSubmittingBillingNote] = useState(false);
  const [commentsExpanded, setCommentsExpanded] = useState(!isCompleted);
  const [billingNotesExpanded, setBillingNotesExpanded] = useState(() => isClient(currentUserInfo?.role));

  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [savingCommentEdit, setSavingCommentEdit] = useState(false);

  const [editingBillingNoteId, setEditingBillingNoteId] = useState(null);
  const [editingBillingNoteText, setEditingBillingNoteText] = useState('');
  const [savingBillingNoteEdit, setSavingBillingNoteEdit] = useState(false);

  const [deleteConfirmMeta, setDeleteConfirmMeta] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const clearCommentEdit = useCallback(() => {
    setEditingCommentId(null);
    setEditingCommentText('');
  }, []);

  const clearBillingNoteEdit = useCallback(() => {
    setEditingBillingNoteId(null);
    setEditingBillingNoteText('');
  }, []);

  useEffect(() => {
    if (!orderId) return undefined;

    const controller = new AbortController();
    const { signal } = controller;
    const billingNotesVisible = showsBillingNotesSection(status);

    setComments([]);
    setBillingNotes([]);
    setLoadingComments(true);
    setLoadingBillingNotes(billingNotesVisible);
    if (refreshKey > 0 && billingNotesVisible) {
      setBillingNotesExpanded(true);
    }

    const loadNotes = async () => {
      const commentsResult = await fetchComments(orderId, { signal });

      if (signal.aborted) return;

      if (commentsResult.error && commentsResult.error !== ABORT_ERROR) {
        toast.error(commentsResult.error);
      } else if (!commentsResult.error) {
        setComments(commentsResult.response || []);
      }
      setLoadingComments(false);

      if (!billingNotesVisible) return;

      const billingResult = await fetchBillingNotes(orderId, { signal });

      if (signal.aborted) return;

      if (billingResult.error && billingResult.error !== ABORT_ERROR) {
        toast.error(billingResult.error);
      } else if (!billingResult.error) {
        setBillingNotes(billingResult.response || []);
      }
      setLoadingBillingNotes(false);
    };

    loadNotes();

    return () => controller.abort();
  }, [orderId, status, refreshKey]);

  useEffect(() => {
    setCommentsExpanded(!isCompletedOrderStatus(status));
    setBillingNotesExpanded(isClient(currentUserInfo?.role));
    setCommentText('');
    setBillingNoteText('');
    clearCommentEdit();
    clearBillingNoteEdit();
    setDeleteConfirmMeta(null);
  }, [orderId, status, currentUserInfo?.role, clearCommentEdit, clearBillingNoteEdit]);

  const handlePostComment = async () => {
    const text = commentText.trim();
    if (!text || submittingComment) return;

    setSubmittingComment(true);
    const { response, error } = await createComment({ orderId, text });
    if (error) {
      toast.error(error);
    } else {
      setComments(prev => [...prev, response]);
      setCommentText('');
      toast.success('Comment posted');
    }
    setSubmittingComment(false);
  };

  const handlePostBillingNote = async () => {
    const text = billingNoteText.trim();
    if (!text || submittingBillingNote) return;

    setSubmittingBillingNote(true);
    const { response, error } = await createBillingNote({ orderId, text });
    if (error) {
      toast.error(error);
    } else {
      setBillingNotes(prev => [...prev, response]);
      setBillingNoteText('');
      toast.success('Billing note posted');
    }
    setSubmittingBillingNote(false);
  };

  const handleCommentEditStart = item => {
    clearBillingNoteEdit();
    setEditingCommentId(item._id);
    setEditingCommentText(item.text || '');
  };

  const handleCommentEditSave = async () => {
    const text = editingCommentText.trim();
    if (!editingCommentId || !text || savingCommentEdit) return;

    setSavingCommentEdit(true);
    const { response, error } = await updateComment(editingCommentId, text);
    if (error) {
      toast.error(error);
    } else {
      setComments(prev => prev.map(c => (c._id === editingCommentId ? response : c)));
      clearCommentEdit();
      toast.success('Comment updated');
    }
    setSavingCommentEdit(false);
  };

  const handleBillingNoteEditStart = item => {
    clearCommentEdit();
    setEditingBillingNoteId(item._id);
    setEditingBillingNoteText(item.text || '');
  };

  const handleBillingNoteEditSave = async () => {
    const text = editingBillingNoteText.trim();
    if (!editingBillingNoteId || !text || savingBillingNoteEdit) return;

    setSavingBillingNoteEdit(true);
    const { response, error } = await updateBillingNote(editingBillingNoteId, text);
    if (error) {
      toast.error(error);
    } else {
      setBillingNotes(prev => prev.map(n => (n._id === editingBillingNoteId ? response : n)));
      clearBillingNoteEdit();
      toast.success('Billing note updated');
    }
    setSavingBillingNoteEdit(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmMeta) return;

    const { type, item } = deleteConfirmMeta;
    setIsDeleting(true);

    if (type === 'comment') {
      const { error } = await deleteComment(item._id);
      if (error) {
        toast.error(error);
      } else {
        setComments(prev => prev.filter(c => c._id !== item._id));
        if (editingCommentId === item._id) clearCommentEdit();
        toast.success('Comment deleted');
        setDeleteConfirmMeta(null);
      }
    } else {
      const { error } = await deleteBillingNote(item._id);
      if (error) {
        toast.error(error);
      } else {
        setBillingNotes(prev => prev.filter(n => n._id !== item._id));
        if (editingBillingNoteId === item._id) clearBillingNoteEdit();
        toast.success('Billing note deleted');
        setDeleteConfirmMeta(null);
      }
    }

    setIsDeleting(false);
  };

  if (!orderId || !orderType) return null;

  const editProps = {
    currentUserInfo,
    disabled: disabled || isDeleting
  };

  return (
    <>
      <div className="mt-3">
        <CommentsPanel
          orderType={orderType}
          comments={comments}
          loading={loadingComments}
          text={commentText}
          onTextChange={setCommentText}
          onSubmit={handlePostComment}
          submitting={submittingComment}
          collapsible={isCompleted}
          expanded={commentsExpanded}
          onToggle={() => setCommentsExpanded(prev => !prev)}
          editingId={editingCommentId}
          editingText={editingCommentText}
          isSavingEdit={savingCommentEdit}
          onEditStart={handleCommentEditStart}
          onEditCancel={clearCommentEdit}
          onEditSave={handleCommentEditSave}
          onEditingTextChange={setEditingCommentText}
          onDeleteClick={item => setDeleteConfirmMeta({ type: 'comment', item })}
          {...editProps}
        />

        {showBillingNotes && (
          <BillingNotesPanel
            orderType={orderType}
            billingNotes={billingNotes}
            loading={loadingBillingNotes}
            text={billingNoteText}
            onTextChange={setBillingNoteText}
            onSubmit={handlePostBillingNote}
            submitting={submittingBillingNote}
            expanded={billingNotesExpanded}
            onToggle={() => setBillingNotesExpanded(prev => !prev)}
            editingId={editingBillingNoteId}
            editingText={editingBillingNoteText}
            isSavingEdit={savingBillingNoteEdit}
            onEditStart={handleBillingNoteEditStart}
            onEditCancel={clearBillingNoteEdit}
            onEditSave={handleBillingNoteEditSave}
            onEditingTextChange={setEditingBillingNoteText}
            onDeleteClick={item => setDeleteConfirmMeta({ type: 'billing', item })}
            {...editProps}
          />
        )}
      </div>

      <AlertModal
        show={deleteConfirmMeta != null}
        onHide={() => !isDeleting && setDeleteConfirmMeta(null)}
        onDismissClick={() => !isDeleting && setDeleteConfirmMeta(null)}
        onContinueClick={handleDeleteConfirm}
        dismissButtonText="Cancel"
        continueButtonText="Delete"
        continueButtonVariant="danger"
        alertText={`Delete this ${deleteConfirmMeta?.type === 'billing' ? 'billing note' : 'comment'}?`}
        showProgress={isDeleting}
        progressText={`Deleting ${deleteConfirmMeta?.type === 'billing' ? 'billing note' : 'comment'}...`}
      />
    </>
  );
};

export default OrderNotesSection;
